import { useState } from 'react';
import type { FormEvent } from 'react';

import { InlineFetchingIndicator, Modal } from '../../../app/components/foundation';
import { ControlPlaneDataSurface } from '../../../app/components/foundation/control-plane';
import type {
  CreateTenantDomainInput,
  TenantDomain,
  UpdateTenantDomainStateInput,
} from '../types/tenant-domain.types';

export interface DomainTableProps {
  domains: TenantDomain[];
  loading: boolean;
  error: string | null;
  submitting: boolean;
  mutationError: string | null;
  onCreate: (input: CreateTenantDomainInput) => Promise<unknown>;
  onUpdate: (domainId: number | string, input: UpdateTenantDomainStateInput) => Promise<unknown>;
}

export function DomainTable({
  domains,
  loading,
  error,
  submitting,
  mutationError,
  onCreate,
  onUpdate,
}: DomainTableProps) {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [domain, setDomain] = useState('');
  const [isPrimary, setIsPrimary] = useState(true);
  const [validationError, setValidationError] = useState<string | null>(null);

  function resetForm() {
    setDomain('');
    setIsPrimary(true);
    setValidationError(null);
  }

  function closeCreate() {
    if (submitting) return;
    resetForm();
    setIsCreateOpen(false);
  }

  async function handleCreate(event: FormEvent) {
    event.preventDefault();
    const normalizedDomain = domain.trim().toLowerCase();
    if (!normalizedDomain || !normalizedDomain.includes('.')) {
      setValidationError('دامنه معتبر وارد کنید.');
      return;
    }

    setValidationError(null);
    try {
      await onCreate({ domain: normalizedDomain, is_primary: isPrimary });
      closeCreate();
    } catch {
      // The hook exposes the backend error below the table.
    }
  }

  async function toggleActive(item: TenantDomain) {
    if (item.id === null) return;
    await onUpdate(item.id, {
      is_active: !item.is_active,
      is_primary: item.is_primary,
    });
  }

  async function makePrimary(item: TenantDomain) {
    if (item.id === null) return;
    await onUpdate(item.id, {
      is_active: item.is_active,
      is_primary: true,
    });
  }

  return (
    <ControlPlaneDataSurface className="vania-tenant-domains-card">
      <div className="vania-tenant-domains-header">
        <h2 className="vania-tenant-workspace-card-title">دامنه‌های تننت</h2>
        <button
          type="button"
          className="vania-tenant-create-submit"
          onClick={() => setIsCreateOpen(true)}
          disabled={submitting}
        >
          افزودن دامنه
        </button>
      </div>

      {loading && <InlineFetchingIndicator />}
      {!loading && error && <p className="vania-tenants-status vania-tenants-status-error">{error}</p>}
      {!loading && !error && domains.length === 0 && (
        <p className="vania-tenants-status">دامنه‌ای برای این تننت ثبت نشده است.</p>
      )}

      {!loading && !error && domains.length > 0 && (
        <div className="vania-tenant-domains-table-scroll">
          <table className="vania-tenant-domains-table">
            <thead>
              <tr>
                <th>دامنه</th>
                <th>وضعیت</th>
                <th>تأیید</th>
                <th>اصلی</th>
                <th>عملیات</th>
              </tr>
            </thead>
            <tbody>
              {domains.map((item) => (
                <tr key={item.id ?? item.domain}>
                  <td dir="ltr">{item.domain}</td>
                  <td>{item.is_active ? 'فعال' : 'غیرفعال'}</td>
                  <td>{item.verification_status === 'active' ? 'تأیید شده' : item.verification_status}</td>
                  <td>{item.is_primary ? 'بله' : 'خیر'}</td>
                  <td className="vania-tenant-domain-actions">
                    <button type="button" onClick={() => void toggleActive(item)} disabled={submitting}>
                      {item.is_active ? 'غیرفعال کردن' : 'فعال کردن'}
                    </button>
                    {!item.is_primary && (
                      <button type="button" onClick={() => void makePrimary(item)} disabled={submitting}>
                        انتخاب به‌عنوان اصلی
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {mutationError && <p className="vania-tenants-status vania-tenants-status-error">{mutationError}</p>}

      <Modal open={isCreateOpen} onClose={closeCreate} title="افزودن دامنه">
        <form className="vania-tenant-create-form" onSubmit={handleCreate}>
          <label className="vania-tenant-create-field">
            <span>دامنه سفارشی</span>
            <input
              type="text"
              dir="ltr"
              value={domain}
              onChange={(event) => setDomain(event.target.value)}
              placeholder="tenant.example.com"
              disabled={submitting}
              autoFocus
            />
          </label>
          <label className="vania-tenant-domain-checkbox">
            <input
              type="checkbox"
              checked={isPrimary}
              onChange={(event) => setIsPrimary(event.target.checked)}
              disabled={submitting}
            />
            دامنه اصلی باشد
          </label>
          {(validationError || mutationError) && (
            <p className="vania-tenant-create-error">{validationError ?? mutationError}</p>
          )}
          <div className="vania-tenant-create-actions">
            <button type="button" className="vania-tenant-create-cancel" onClick={closeCreate} disabled={submitting}>
              انصراف
            </button>
            <button type="submit" className="vania-tenant-create-submit" disabled={submitting}>
              {submitting ? 'در حال ثبت...' : 'ثبت دامنه'}
            </button>
          </div>
        </form>
      </Modal>
    </ControlPlaneDataSurface>
  );
}
